// V2 lobby card. Surfaces the network-mode connection state to a
// non-developer playtester:
//
//   - Connecting       — joining the Colyseus room.
//   - Waiting           — joined; waiting for the second player.
//   - Connected         — first snapshot received; about to transition
//                          to GameScene.
//   - Bot-filled (V2.5) — the Quick Play matchmaker filled the empty
//                          slot with a server-side bot. Lobby honestly
//                          tells the player so they're not surprised
//                          (docs/08_MULTIPLAYER.md "Empty-lobby trap").
//   - Failed            — connection error; "Retry" reloads the page.
//
// Owns its own DOM root above the Phaser canvas (Rule 7: no Phaser
// gameplay state on UI). The host (MainMenuScene) reaches in via the
// imperative `setStatus` / `show` / `hide` methods rather than props
// — there is no reactive framework in V1/V2.
//
// Reuses the `main-menu__*` segmented-control styles MainMenuScene
// injects so visual continuity holds.

import { appendMainMenuBackdrop } from './mainMenuBackdrop.js';
import { injectMainMenuStyles } from './mainMenuStyles.js';

const ROOT_ID = 'network-lobby';

export type NetworkLobbyStatus =
  // V2.5+ pre-join state. Lobby shows the room code + opt-in
  // checkbox + a "Join match" button. The connect is DEFERRED until
  // the user clicks the button, so configuration (Reconnect-Bot
  // opt-in) is captured before the joinOrCreate. Without this state
  // the V2 lobby auto-connected on mount and any opt-in toggle the
  // user did afterwards never reached the server (the join options
  // were already locked in).
  | 'idle'
  | 'connecting'
  | 'waiting'
  | 'connected'
  | 'bot_filled'
  | 'failed';

export interface NetworkLobbyOptions {
  // For private rooms, the shareable code (e.g., "ABCD"). For Quick
  // Play, the caller passes a static label like "Quick Play" so the
  // card has something to show; the queue has no shareable code.
  roomCode: string;
  fullUrl: string; // copied to clipboard by the "Copy link" button
  // V2.5: Quick Play has no shareable URL — hide the Copy-link button
  // so the player doesn't share a link that just opens someone else's
  // queue.
  hideCopyLink?: boolean;
  // V2.5+ Reconnect-Bot opt-in. Initial state of the checkbox; the
  // change handler fires whenever the user toggles it. The lobby is
  // presentation-only — selection state is owned by the host scene
  // (MainMenuScene / ClientApp) per Rule 1. Pass `undefined` to hide
  // the checkbox entirely (e.g., for build configs where Reconnect-Bot
  // is disabled).
  allowBotReconnectFill?: boolean;
  onAllowBotReconnectFillChange?: (allow: boolean) => void;
  // V2.5+ deferred-join. When provided, the lobby renders a "Join
  // match" button and stays in `idle` until the button is clicked
  // (calls onJoin). The host scene starts the match in the onJoin
  // handler, which in turn flips status to `connecting`. Omit (V2
  // legacy callers) to keep the auto-join behaviour where the host
  // calls `startMatch` immediately after `show()`.
  onJoin?: () => void;
  onCancel: () => void;
  onRetry: () => void;
  // V1.7 (2.15): Quick Play bot-fill disclosure. When set, the lobby
  // tells the player up-front that if no human opponent arrives within
  // this many seconds, a bot fills the empty slot — matches the
  // server-side `SIGNALCLASH_QUICKPLAY_FILL_MS` window (production 15
  // s). Drives the help text *and* the live "waiting" status label so
  // the player isn't blindsided by the eventual `bot_filled` flip.
  // Omit for private rooms (no opening bot-fill there — the mid-match
  // reconnect-bot path is covered by the opt-in checkbox instead).
  botFillAfterSec?: number;
}

const STATUS_LABEL: Record<NetworkLobbyStatus, string> = {
  idle: 'Configure and click Join when ready',
  connecting: 'Connecting…',
  waiting: 'Waiting for opponent…',
  connected: 'Connected. Starting match…',
  bot_filled: 'Filled by bot — match starting',
  failed: 'Connection failed',
};

// V1.7 (2.15): When `botFillAfterSec` is set on the lobby, the
// "waiting" label discloses the bot-fill window. Other statuses
// pass through unchanged.
function statusLabelFor(status: NetworkLobbyStatus, botFillAfterSec?: number): string {
  if (status === 'waiting' && botFillAfterSec !== undefined) {
    return `Waiting for opponent — bot fills in ~${botFillAfterSec}s`;
  }
  return STATUS_LABEL[status];
}

export class NetworkLobby {
  private opts: NetworkLobbyOptions;
  private root: HTMLDivElement | null = null;
  private statusEl: HTMLDivElement | null = null;
  private detailEl: HTMLDivElement | null = null;
  private copyBtn: HTMLButtonElement | null = null;
  private cancelBtn: HTMLButtonElement | null = null;
  private retryBtn: HTMLButtonElement | null = null;
  private allowBotCheckbox: HTMLInputElement | null = null;
  private allowBotChangeHandler: (() => void) | null = null;
  private joinBtn: HTMLButtonElement | null = null;
  private joinHandler: (() => void) | null = null;
  private cancelHandler: (() => void) | null = null;
  private retryHandler: (() => void) | null = null;
  private copyHandler: (() => void) | null = null;
  // Initial status: `idle` when the host scene wired an `onJoin`
  // callback (deferred-join flow); `connecting` otherwise (legacy
  // auto-join flow). The host scene flips to `connecting` /
  // `waiting` etc. as the connection progresses.
  private status: NetworkLobbyStatus;
  private detail = '';

  constructor(opts: NetworkLobbyOptions) {
    this.opts = opts;
    this.status = opts.onJoin !== undefined ? 'idle' : 'connecting';
  }

  show(): void {
    injectMainMenuStyles();
    this.unmount();
    this.mount();
  }

  hide(): void {
    this.unmount();
  }

  setStatus(status: NetworkLobbyStatus, detail = ''): void {
    this.status = status;
    this.detail = detail;
    this.applyStatus();
  }

  // V4.1 — late-binding share URL. The CrazyGames SDK's `inviteLink()`
  // is async (the portal mints a proper `www.crazygames.com/game/...`
  // URL that bypasses the game-files Cloudflare WAF). MainMenuScene
  // mounts the lobby synchronously with `window.location.href` as a
  // fallback, then resolves the real URL and pushes it here so the
  // Copy / Share buttons send the correct invite. No-op when called
  // before mount; subsequent mounts read from `this.opts.fullUrl`.
  setFullUrl(url: string): void {
    this.opts = { ...this.opts, fullUrl: url };
  }

  private applyStatus(): void {
    if (this.statusEl !== null) {
      this.statusEl.textContent = statusLabelFor(this.status, this.opts.botFillAfterSec);
      this.statusEl.dataset.status = this.status;
    }
    if (this.detailEl !== null) {
      this.detailEl.textContent = this.detail;
    }
    if (this.retryBtn !== null) {
      this.retryBtn.style.display = this.status === 'failed' ? '' : 'none';
    }
    // Join button: only visible in the idle state (pre-click). After
    // the user has joined, the connection is live and they can't
    // re-trigger the join.
    if (this.joinBtn !== null) {
      this.joinBtn.style.display = this.status === 'idle' ? '' : 'none';
    }
    // Opt-in checkbox: disable after the user has clicked Join. The
    // server reads the flag at `onJoin` time, so flipping it
    // post-connect has no effect (V3+ would need a `set_opt_in`
    // command per Rule 6 to allow mid-match changes).
    if (this.allowBotCheckbox !== null) {
      this.allowBotCheckbox.disabled = this.status !== 'idle';
    }
  }

  private mount(): void {
    const root = ensureRoot();
    root.innerHTML = '';
    root.style.display = 'flex';
    appendMainMenuBackdrop(root);
    this.root = root;

    const card = document.createElement('div');
    card.className = 'main-menu__card';

    const title = document.createElement('h1');
    title.className = 'main-menu__title main-menu__title--compact';
    title.textContent = this.opts.hideCopyLink === true ? 'SIGNAL CLASH' : 'Private 1v1 Lobby';
    card.appendChild(title);

    const tagline = document.createElement('div');
    tagline.className = 'main-menu__tagline';
    tagline.textContent = this.opts.hideCopyLink === true ? 'V2 — Multiplayer' : 'Invite a friend using this link';
    card.appendChild(tagline);

    const codeLabel = document.createElement('div');
    codeLabel.className = 'main-menu__diff-label';
    codeLabel.textContent = this.opts.hideCopyLink === true ? 'Queue' : 'Private room code';
    card.appendChild(codeLabel);

    const codeValue = document.createElement('div');
    codeValue.className = 'network-lobby__code';
    codeValue.textContent = this.opts.roomCode;
    card.appendChild(codeValue);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'network-lobby__status';
    this.statusEl.dataset.status = this.status;
    this.statusEl.textContent = statusLabelFor(this.status, this.opts.botFillAfterSec);
    card.appendChild(this.statusEl);

    this.detailEl = document.createElement('div');
    this.detailEl.className = 'main-menu__diff-blurb';
    this.detailEl.textContent = this.detail;
    card.appendChild(this.detailEl);

    // V2.5+ Reconnect-Bot opt-in. Lives BEFORE join (lobby), not in-
    // match — once the match starts the flag is fixed for the session.
    // V3+ may add an in-match `Command` to flip it, but the pre-join
    // checkbox is the simplest UX that ships in V2.5+ (per Rule 6 an
    // in-match toggle would need to flow through `Command`, not a
    // side channel). Default OFF; the player must explicitly opt in.
    if (this.opts.onAllowBotReconnectFillChange !== undefined) {
      const optInLabel = document.createElement('label');
      optInLabel.className = 'network-lobby__opt-in';
      this.allowBotCheckbox = document.createElement('input');
      this.allowBotCheckbox.type = 'checkbox';
      this.allowBotCheckbox.checked = this.opts.allowBotReconnectFill === true;
      this.allowBotChangeHandler = (): void => {
        if (this.allowBotCheckbox === null) return;
        this.opts.onAllowBotReconnectFillChange?.(this.allowBotCheckbox.checked);
      };
      this.allowBotCheckbox.addEventListener('change', this.allowBotChangeHandler);
      optInLabel.appendChild(this.allowBotCheckbox);
      const optInText = document.createElement('span');
      optInText.textContent = 'Allow a bot to play my slot if I disconnect';
      optInLabel.appendChild(optInText);
      card.appendChild(optInLabel);
    }

    const buttons = document.createElement('div');
    buttons.className = 'network-lobby__buttons';

    if (this.opts.hideCopyLink !== true) {
      this.copyBtn = document.createElement('button');
      this.copyBtn.type = 'button';
      this.copyBtn.className = 'main-menu__btn';
      this.copyBtn.innerHTML = '<span class="main-menu__btn-main"><span>Copy Invite Link</span></span>';
      this.copyHandler = (): void => {
        void copyToClipboard(this.opts.fullUrl).then((ok) => {
          if (this.copyBtn === null) return;
          const before = this.copyBtn.innerHTML;
          this.copyBtn.innerHTML = `<span class="main-menu__btn-main"><span>${ok ? 'Link copied!' : 'Copy failed'}</span></span>`;
          setTimeout(() => {
            if (this.copyBtn !== null) this.copyBtn.innerHTML = before;
          }, 1200);
        });
      };
      this.copyBtn.addEventListener('click', this.copyHandler);
      buttons.appendChild(this.copyBtn);

      // V3 mobile: if the browser supports the Web Share API (Android Chrome,
      // iOS Safari), surface a native "Share" button alongside Copy. This is
      // the canonical mobile invite flow — tap "Share" → pick a chat app →
      // friend gets the link without copy/paste-into-keyboard. Falls back to
      // hidden on browsers without support (most desktops).
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        const shareBtn = document.createElement('button');
        shareBtn.type = 'button';
        shareBtn.className = 'main-menu__btn';
        shareBtn.innerHTML = '<span class="main-menu__btn-main"><span>Share</span></span>';
        shareBtn.addEventListener('click', () => {
          // Web Share rejects if the user dismisses the picker — swallow the
          // rejection since cancel is a valid outcome, not an error.
          void (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }).share?.({
            title: 'Signal Clash',
            text: 'Join my Signal Clash match',
            url: this.opts.fullUrl,
          }).catch(() => undefined);
        });
        buttons.appendChild(shareBtn);
      }
    }

    // V2.5+ deferred-join button. Only mounted when the host scene
    // wired an `onJoin` callback. Hidden once status leaves `idle`
    // (visibility managed in `applyStatus`).
    if (this.opts.onJoin !== undefined) {
      this.joinBtn = document.createElement('button');
      this.joinBtn.type = 'button';
      this.joinBtn.className = 'main-menu__btn main-menu__btn--primary';
      this.joinBtn.innerHTML = '<span class="main-menu__btn-main"><span>Join Match</span></span>';
      this.joinBtn.style.display = this.status === 'idle' ? '' : 'none';
      this.joinHandler = (): void => {
        this.opts.onJoin?.();
      };
      this.joinBtn.addEventListener('click', this.joinHandler);
      buttons.appendChild(this.joinBtn);
    }

    this.retryBtn = document.createElement('button');
    this.retryBtn.type = 'button';
    this.retryBtn.className = 'main-menu__btn main-menu__btn--primary';
    this.retryBtn.innerHTML = '<span class="main-menu__btn-main"><span>Retry</span></span>';
    this.retryBtn.style.display = this.status === 'failed' ? '' : 'none';
    this.retryHandler = (): void => this.opts.onRetry();
    this.retryBtn.addEventListener('click', this.retryHandler);
    buttons.appendChild(this.retryBtn);

    this.cancelBtn = document.createElement('button');
    this.cancelBtn.type = 'button';
    this.cancelBtn.className = 'main-menu__btn main-menu__btn--red';
    this.cancelBtn.innerHTML = '<span class="main-menu__btn-main"><span>Cancel</span></span>';
    this.cancelHandler = (): void => this.opts.onCancel();
    this.cancelBtn.addEventListener('click', this.cancelHandler);
    buttons.appendChild(this.cancelBtn);

    card.appendChild(buttons);

    const help = document.createElement('div');
    help.className = 'main-menu__help';
    // V1.7 (2.15): when bot-fill is enabled (Quick Play), the help text
    // *leads* with the bot-fill disclosure so the player learns about it
    // before clicking Join — not after the timer has already fired.
    if (this.opts.botFillAfterSec !== undefined) {
      help.textContent = `Queueing for an online opponent. If none joins within ~${this.opts.botFillAfterSec}s, a bot fills your match so you can still play.`;
    } else if (this.opts.hideCopyLink === true) {
      help.textContent = 'Queueing for an online opponent. Match starts when both have joined.';
    } else {
      help.textContent = 'Send the invite link to a friend. Waiting for the second player to join.';
    }
    card.appendChild(help);

    root.appendChild(card);
    injectStyles();
  }

  private unmount(): void {
    if (this.copyBtn !== null && this.copyHandler !== null) {
      this.copyBtn.removeEventListener('click', this.copyHandler);
    }
    if (this.cancelBtn !== null && this.cancelHandler !== null) {
      this.cancelBtn.removeEventListener('click', this.cancelHandler);
    }
    if (this.retryBtn !== null && this.retryHandler !== null) {
      this.retryBtn.removeEventListener('click', this.retryHandler);
    }
    if (this.allowBotCheckbox !== null && this.allowBotChangeHandler !== null) {
      this.allowBotCheckbox.removeEventListener('change', this.allowBotChangeHandler);
    }
    if (this.joinBtn !== null && this.joinHandler !== null) {
      this.joinBtn.removeEventListener('click', this.joinHandler);
    }
    this.copyBtn = null;
    this.cancelBtn = null;
    this.retryBtn = null;
    this.joinBtn = null;
    this.allowBotCheckbox = null;
    this.statusEl = null;
    this.detailEl = null;
    this.copyHandler = null;
    this.cancelHandler = null;
    this.retryHandler = null;
    this.joinHandler = null;
    this.allowBotChangeHandler = null;
    if (this.root !== null) {
      this.root.style.display = 'none';
      this.root.innerHTML = '';
      this.root = null;
    }
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

async function copyToClipboard(value: string): Promise<boolean> {
  // navigator.clipboard requires a user-initiated event, which the
  // copy button click is. In secure contexts (https / localhost) this
  // resolves; otherwise we fall through to false.
  if (typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

const STYLE_ID = 'network-lobby-styles';

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 30;
  font-family: var(--mm-font-ui, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace);
  color: var(--mm-ink, #cfd6dd);
  background: #02060d;
}
.network-lobby__code {
  font-size: 22px;
  letter-spacing: 0.2em;
  color: var(--mm-amber, #f5e76e);
  text-shadow: 0 0 12px rgba(245,231,110,0.25);
  margin: 4px 0 6px;
  text-align: center;
}
.network-lobby__status {
  font-size: 12px;
  color: var(--mm-ink-muted, #8c95a0);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  min-height: 14px;
  text-align: center;
}
.network-lobby__status[data-status="idle"] { color: var(--mm-amber, #f5e76e); }
.network-lobby__status[data-status="connected"] { color: var(--mm-green, #6ed788); }
.network-lobby__status[data-status="waiting"] { color: var(--mm-blue-bright, #6ed4f5); }
.network-lobby__status[data-status="bot_filled"] { color: var(--mm-red-bright, #f5b76e); }
.network-lobby__status[data-status="failed"] { color: #e07b7b; }
.network-lobby__opt-in input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.network-lobby__opt-in input:disabled + span {
  opacity: 0.5;
}
.network-lobby__buttons {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.network-lobby__opt-in {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 11px;
  color: var(--mm-ink-muted, #8c95a0);
  letter-spacing: 0.04em;
  cursor: pointer;
  user-select: none;
}
.network-lobby__opt-in input {
  accent-color: var(--mm-blue-bright, #6fdcff);
  cursor: pointer;
}
/* V3 mobile: lobby card uses .main-menu__card so the MainMenu responsive
 * rules already shrink the card. We only need to add a wrap so a Copy +
 * Share + Cancel + Retry row doesn't overflow on phones, and slightly
 * bigger buttons. The opt-in label is the tap target for the checkbox —
 * give it a 44 px minimum height so fingers can reliably toggle it. */
@media (max-width: 820px), (pointer: coarse) {
  .network-lobby__code { font-size: 18px; letter-spacing: 0.16em; }
  .network-lobby__status { font-size: 11px; }
  .network-lobby__opt-in { min-height: 44px; }
  .network-lobby__buttons { flex-wrap: wrap; justify-content: center; gap: 6px; }
  .network-lobby__buttons .main-menu__btn {
    min-height: 44px;
    padding: 10px 18px;
    font-size: 12px;
    margin-top: 0;
    flex: 1 1 auto;
    max-width: 160px;
  }
}
`;
  document.head.appendChild(style);
}
